<template>
    <BasePage :model="model.hero">
        <div class="charities-page">
            <div class="map-container">
                <GoogleMap :api-key="apiKey" :map-id="MAP_ID" :zoom="mapZoom" class="google-map" :center="mapCenter"
                    :map-type-control="false" :zoom-control="true">
                    <AdvancedMarker v-for="value in hospices?.hospices"
                        :options="{ position: value.location.coordinates }" @click="() => onMarkerClick(value)">
                    </AdvancedMarker>
                    <div class="search-bar">
                        <Combobox v-model="selectedHospice" by="nodeId" openOnClick class="w-full"
                            :ignore-filter="true">
                            <ComboboxAnchor class="w-full">
                                <div class="relative w-full items-center">
                                    <ComboboxInput class="pl-2 w-full" v-model="searchTerm"
                                        :display-value="(val) => val?.name ?? ''" placeholder="Search for charity" />
                                    <span class="absolute start-0 inset-y-0 flex items-center justify-center px-3">
                                        <Search class="size-4 text-muted-foreground" />
                                    </span>
                                </div>
                            </ComboboxAnchor>
                            <ComboboxList class="h-[300px] overflow-y-auto" side="bottom" align="start"
                                :avoid-collisions="false">
                                <ComboboxViewport>
                                    <ComboboxEmpty>
                                        No charity found.
                                    </ComboboxEmpty>
                                    <ComboboxGroup>
                                        <ComboboxItem v-for="hospice in filteredHospices" :key="hospice.nodeId"
                                            :value="hospice">
                                            {{ hospice.name }}
                                            <ComboboxItemIndicator>
                                                <Check class="ml-auto size-4" />
                                            </ComboboxItemIndicator>
                                        </ComboboxItem>
                                    </ComboboxGroup>
                                </ComboboxViewport>
                            </ComboboxList>
                        </Combobox>
                    </div>
                </GoogleMap>
                <div class="hospice-info" v-show="selectedHospice" ref="hospiceInfoRef">
                    <div v-if="isFetching">loading</div>
                    <div class="card">
                        <div class="top">
                            <Button @click="resetSelectedHospice">
                                <X />
                            </Button>
                        </div>
                        <div class="main-card">
                            <div class="name">
                                <img :src="currentHospice?.logo.url" :alt="currentHospice?.logo.text" />
                                <h2>{{ currentHospice?.name }}</h2>
                            </div>
                            <div>{{ currentHospice?.description }}</div>
                            <div class="contact">
                                <div class="contact-row" v-if="currentHospice?.contact.collectionDateStart">
                                    <img :src="`${rootPath}/static/icons/tree.png`" alt="collection"></img>
                                    <span v-if="currentHospice?.contact.collectionDateEnd">
                                        {{
                                            currentHospice?.contact.translations["hospice.Contact.CollectionOpenAndCloseDate"]
                                        }}
                                        <span class="date">{{ longLocalDate(culture,
                                            currentHospice?.contact.collectionDateStart) }}</span> -
                                        <span class="date">{{ longLocalDate(culture,
                                            currentHospice?.contact.collectionDateEnd)
                                        }}</span>
                                    </span>
                                    <span v-else class="date"> {{
                                        currentHospice?.contact.translations["hospice.Contact.CollectionOnlyOpenDate"]
                                    }}
                                        {{ longLocalDate(culture, currentHospice?.contact.collectionDateStart) }}</span>
                                </div>
                                <div class="contact-row">
                                    <img :src="`${rootPath}/static/icons/pointer.png`" alt="address"></img>
                                    <div>{{ currentHospice?.contact.address }}</div>
                                </div>
                                <div class="contact-row">
                                    <img :src="`${rootPath}/static/icons/link.png`" alt="link"></img>
                                    <a :href="currentHospice?.contact.website?.url" target="_blank">{{
                                        currentHospice?.contact.website?.text }}</a>
                                </div>
                            </div>
                            <Button as-child><a :href="currentHospice?.detailsPageUrl">{{
                                model.translations['hospiceList.ViewHospiceDetailPageButtonText'] }}</a></Button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="statistics-wrapper">
                <h3>{{ model.statistics.title }}</h3>
                <div v-html="model.statistics.description"></div>
                <div class="statistics">
                    <div v-for="statistic in model.statistics.statistics" class="statistics-item">
                        <div class="statistics-item-wrapper">
                            <img :src="statistic.icon" class="statistics-item-image" :alt="statistic.label"
                                :title="statistic.value" />
                            <span class="statistics-item-value">{{ statistic.value }}</span>
                        </div>
                        <span class="statistics-item-label">{{ statistic.label }}</span>
                    </div>
                </div>
            </div>
        </div>
    </BasePage>
</template>
<script setup lang="ts">
import BasePage from '@/components/Common/BasePage/BasePage.vue';
import { IHospiceListProps } from './HospiceList.model';
import { AdvancedMarker, GoogleMap } from 'vue3-google-map';
import { useQuery } from '@tanstack/vue-query';
import { getHospiceDetails, getHospiceList, IHospiceItem } from '@/api/hospice';
import { computed, nextTick, ref, watch, } from 'vue';
import { MAP_CENTER, MAP_ZOOM } from "./HospiceList.constant"
import { Combobox, ComboboxAnchor, ComboboxEmpty, ComboboxGroup, ComboboxInput, ComboboxItem, ComboboxItemIndicator, ComboboxList, ComboboxViewport } from '@/components/ui/combobox';
import { Check, Search, X } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { MAP_ID } from '@/contants/constant';
import { longLocalDate } from '@/share/util';

const selectedHospice = ref<IHospiceItem | undefined>();
const searchTerm = ref('');
const hospiceInfoRef = ref<HTMLElement | null>(null);

const { data: hospices } = useQuery({ queryKey: ["getHospicies"], queryFn: () => getHospiceList(model.searchHospiceByNameUrl) })
const { data: currentHospice, isFetching } = useQuery({
    queryKey: ["currentHospice", selectedHospice],
    queryFn: () => getHospiceDetails(model.getHospiceByIdUrl, { nodeId: selectedHospice.value?.nodeId ?? "" }),
    enabled: computed(() => !!selectedHospice.value?.nodeId)
})

const { model } = defineProps<IHospiceListProps>()
const apiKey = window.__STATE__?.mapKey ?? undefined
const rootPath = window.__STATE__?.rootPath
const culture = window.__STATE__?.culture ?? "en-GB"

const mapCenter = computed(() => currentHospice.value?.contact.location.center ?? MAP_CENTER)
const mapZoom = computed(() => {
    return currentHospice.value?.contact.location.zoom ?? MAP_ZOOM
})

const resetSelectedHospice = () => {
    selectedHospice.value = undefined
}

const filteredHospices = computed(() => {
    if (!hospices.value?.hospices) return [];
    if (!searchTerm.value) {
        return hospices.value.hospices;
    }

    const lowerCaseSearch = searchTerm.value.toLowerCase();

    return hospices.value.hospices.filter(hospice => {
        const nameMatch = hospice.name.toLowerCase().includes(lowerCaseSearch);
        const postcodeMatch = hospice.postcodes?.some(postcode =>
            postcode.toLowerCase().includes(lowerCaseSearch)
        );

        return nameMatch || postcodeMatch;
    });
});

const onMarkerClick = (hospiceItem: IHospiceItem) => {
    selectedHospice.value = hospiceItem
}


watch(selectedHospice, (newHospice, oldHospice) => {
    if (newHospice && !oldHospice) {
        if (window.innerWidth < 768) {
            nextTick(() => {
                hospiceInfoRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        }
    }
});

</script>
<style lang="scss" scoped>
.statistics-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px;

    h3 {
        color: var(--primary);
        font-size: 1.5rem;
        font-weight: 700;
        margin-bottom: 8px;
    }
}

.statistics {
    display: flex;
    flex-direction: column;
    gap: 20px;
    font-weight: 700;

    .statistics-item {
        display: flex;
        flex-direction: column;
        font-weight: 700;


        .statistics-item-wrapper {
            display: flex;
            align-items: center;
            gap: 5px;
            justify-content: center;
        }

        .statistics-item-image {
            max-width: 40px;
            aspect-ratio: 1;
            object-fit: contain;
        }

        .statistics-item-value {
            color: var(--primary);
            font-size: 1.75rem;
        }

        .statistics-item-label {
            font-size: 1.0625rem;
        }
    }
}

.charities-page {
    border-radius: 0 0 24px 24px;
    border: 1px solid lightgray;
    border-top: 0;
    display: flex;
    flex-direction: column;
}

.map-container {
    display: flex;
    flex-direction: column;
}

.google-map {
    width: 100%;
    height: 600px;
    position: relative;

    .search-bar {
        display: flex;
        gap: 10px;
        position: absolute;
        background-color: white;
        padding: 12px 8px;
        border-radius: 12px;
        top: 20px;
        left: 20px;
        right: 20px;
    }
}

.hospice-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-width: 50%;

    .card {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        padding: 24px 18px;

        .top {
            width: 100%;
            display: flex;
            justify-content: end;
            padding: 10px;
        }
    }

    .main-card {
        display: flex;
        flex-direction: column;
        gap: 20px;
        padding: 16px 24px;
    }

    .name {
        display: flex;
        align-items: center;
        gap: 20px;
        color: var(--primary);

        img {
            width: 100px;
            height: 100px;
            object-fit: contain;
            border-radius: 12px;
        }
    }

    .contact {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .contact-row {
            display: flex;
            gap: 5px;

            img {
                width: 24px;
                height: 24px;
            }

            a {
                color: var(--primary);
                text-decoration: underline;
            }
        }
    }
}

@media (min-width: 768px) {
    .map-container {
        flex-direction: row;

    }

    .statistics {
        flex-direction: row;
    }

    .google-map {

        .search-bar {
            width: 400px;
            right: unset;
        }

        :deep(.mapdiv) {
            border-radius: 0 0 24px 24px;

        }
    }
}
</style>